// ── "Last …" tiles: is the scheduled feed behind this page still running? ──
//
// A worklist that is empty because nothing is wrong and one that is empty
// because the scheduled job stopped look exactly alike. These tiles tell them
// apart: when each feed last ran, red once it is overdue or the last run failed.
//
//   const fresh = useMonitorFreshness();              // one call per page
//   <LastRunTile label="Last CSC pull" run={fresh && fresh.csc} staleHours={48} />
//
// Data: GET /api/getMonitorFreshness. Overdue = no run within `staleHours`
// (48 for the daily UCC and tax jobs, so one missed morning shows; 192 for
// the weekly news search).
import { useState, useEffect } from "react";
import { fmtDate } from "../../../shared/format";
import { useApp } from "../../../shared/appContext";

export const useMonitorFreshness = () => {
  const { api } = useApp();
  const [d, setD] = useState(null);
  useEffect(() => {
    let live = true;
    api.get("getMonitorFreshness").then(x => { if (live) setD(x || {}); }).catch(() => { if (live) setD({}); });
    return () => { live = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return d;
};

const ago = t => {
  const h = (Date.now() - new Date(t).getTime()) / 3600000;
  if (h < 1) return "under an hour ago";
  if (h < 48) return Math.round(h) + " hour" + (Math.round(h) === 1 ? "" : "s") + " ago";
  return Math.round(h / 24) + " days ago";
};

// run: { at, error, by, note }  (null = not loaded yet; {} = never ran)
export const LastRunTile = ({ label, run, staleHours = 48, off }) => {
  const loading = run === undefined || run === null;
  const at = run && run.at;
  const stale = !loading && !off && (!at || (Date.now() - new Date(at).getTime()) / 3600000 > staleHours);
  const failed = !loading && !!(run && run.error);
  const bad = stale || failed;
  const value = loading ? "…" : off ? "off" : at ? fmtDate(at) : "never";
  const sub = loading ? "" : off ? "switched off on Integrations"
    : failed ? "last run failed"
    : at ? ago(at) + (run.by ? " · " + run.by : "") : "no run on record";
  const title = [run && run.error, run && run.note,
                 stale ? `Nothing in the last ${staleHours >= 72 ? Math.round(staleHours / 24) + " days" : staleHours + " hours"} — check the scheduled job.` : null]
    .filter(Boolean).join("\n");
  return (
    <div title={title || undefined}
         style={{ background: bad ? "#FBE4DD" : "var(--color-background-secondary)",
                  border: "0.5px solid " + (bad ? "transparent" : "var(--color-border-tertiary)"),
                  borderRadius: 8, padding: "10px 14px", minWidth: 130 }}>
      <div style={{ fontSize: 11, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: bad ? "#993C1D" : "inherit" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: bad ? "#993C1D" : "var(--color-text-secondary)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
};

// The API rows, as tiles want them.
export const runOf = {
  csc: f => f === null ? null : !f.csc ? {} : {
    at: f.csc.finishedat || f.csc.startedat, by: f.csc.ranby,
    error: f.csc.status === "Failed" ? (f.csc.error || "failed") : null,
    note: f.reports && f.reports.lastreadat ? "Newest search report read " + fmtDate(f.reports.lastreadat) : null },
  tracking: f => f === null ? null : !f.tracking ? {} : {
    at: f.tracking.finishedat || f.tracking.startedat, by: f.tracking.ranby, error: f.tracking.error || null },
  taxguard: f => f === null ? null : !f.taxguard ? {} : {
    at: f.taxguard.lastrunat, error: f.taxguard.lastrunerror || null,
    note: f.taxguard.needsql ? "Run sql/2026-10-29_monitor_last_run.sql to record Tax Guard run times." : f.taxguard.lastsyncstatus },
  news: f => f === null ? null : !f.news ? {} : {
    at: f.news.finishedat || f.news.startedat, by: f.news.ranby,
    // A weekly run with a name or two erroring is not a failed run; one that
    // searched nothing and errored is.
    error: f.news.errors && !f.news.scanned ? (f.news.errormessage || "failed") : null },
};
